import React, { useEffect, useRef, useState } from 'react'
import { InputDiv, Input, SearchOptions, Option } from '../../../../styles/select-style'
import axios from '../../../../../config/axios'
import { formatCnpj, formatCpf } from '../../../../../utils/masks'
import { CompanyItemType, PropsType } from './types'

const companyCache = new Map<string, CompanyItemType[]>()

const SelectCustomerInFreight: React.FC<PropsType> = ({ client, setClient }) => {
  const [items, setItems] = useState<CompanyItemType[]>([])
  const [allCompanies, setAllCompanies] = useState<CompanyItemType[]>([])
  const [inputItem, setInputItem] = useState<string>('')
  const [showOptions, setShowOptions] = useState<boolean>(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const optionsRef = useRef<HTMLUListElement>(null)
  const isFocusedRef = useRef<boolean>(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const getLabelMain = (item: any): string => {
    if (!item) return ''
    const fullItem = allCompanies.find((c) => c.id === item.id) || item
    if (!fullItem || !fullItem.isMain) return ''
    const tipo =
      fullItem.tipoEstabelecimento ||
      (fullItem.cnpj
        ? fullItem.cnpj.replace(/\D/g, '').substring(8, 12) === '0001'
          ? 'Matriz'
          : 'Filial'
        : '')
    return tipo ? ` - ${tipo}` : ''
  }

  const getDisplayName = (val: any): string => {
    if (!val) return ''
    const fullItem = allCompanies.find((c) => c.id === val.id) || val
    const baseName = (fullItem.companyName || fullItem.contactName || fullItem.name || fullItem.fantasia || '') as string
    return `${baseName}${getLabelMain(fullItem)}`
  }

  const getDocument = (item: any): string => {
    if (!item) return ''
    const fullItem = allCompanies.find((c) => c.id === item.id) || item
    const rawDoc = fullItem.cnpj || fullItem.cpf || ''
    if (fullItem.cnpj) return formatCnpj(fullItem.cnpj) || rawDoc
    if (fullItem.cpf) return formatCpf(fullItem.cpf) || rawDoc
    return rawDoc
  }

  useEffect(() => {
    // Não sobrescreve o que a pessoa está digitando: enquanto o campo está
    // focado, mudanças de prop/allCompanies (ex.: retorno da busca com debounce
    // ou pré-seleção assíncrona) não devem limpar/trocar o texto digitado.
    if (isFocusedRef.current) return
    if (client?.id) {
      setInputItem(getDisplayName(client))
    } else {
      setInputItem(client?.companyName || '')
    }
  }, [client?.id, client?.companyName, allCompanies])

  useEffect(() => {
    if (client?.id && allCompanies.length > 0) {
      const found = allCompanies.find((c) => c.id === client.id)
      if (found && ((!client.cnpj && found.cnpj) || (client.isMain === undefined && found.isMain !== undefined))) {
        setClient((prev: any) => ({
          ...found,
          ...prev,
          isMain: found.isMain,
          tipoEstabelecimento: found.tipoEstabelecimento,
          cnpj: found.cnpj,
          cpf: found.cpf,
        }))
      }
    }
  }, [client?.id, allCompanies])

  useEffect(() => {
    let isMounted = true
    const cacheKey = 'all_companies_freight'

    if (companyCache.has(cacheKey)) {
      const cached = companyCache.get(cacheKey) || []
      setAllCompanies(cached)
      setItems(cached)
    }

    axios
      .get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`, {
        params: {
          limit: 1000,
        },
      })
      .then((response) => {
        if (!isMounted) return
        const dataArray = Array.isArray(response.data)
          ? response.data
          : response.data?.data || []
        const companies = Array.isArray(dataArray) ? dataArray : []
        companyCache.set(cacheKey, companies)
        setAllCompanies((prev) => {
          const existingIds = new Set(companies.map((c: any) => c.id))
          const extra = prev.filter((c) => !existingIds.has(c.id))
          return [...companies, ...extra]
        })
        setItems((prev) => {
          if (prev.length === 0) return companies
          return prev
        })
      })
      .catch((error) => {
        console.log(error)
      })

    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        optionsRef.current &&
        inputRef.current &&
        !optionsRef.current.contains(target) &&
        !inputRef.current.contains(target)
      ) {
        setShowOptions(false)
        if (client?.id) {
          setInputItem(getDisplayName(client))
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [client, allCompanies])

  const filterCompanies = (source: CompanyItemType[], term: string): CompanyItemType[] => {
    if (!term) return source
    const cleanTermText = term
      .replace(/\s*-\s*(Matriz|Filial)$/i, '')
      .trim()
      .toLowerCase()
    const cleanDigits = term.replace(/\D/g, '')
    return source.filter((item) => {
      const displayName = getDisplayName(item).toLowerCase()
      const name = (item.companyName || item.contactName || item.name || item.fantasia || '').toLowerCase()
      const cnpj = (item.cnpj || '').toLowerCase()
      const cleanCnpj = cnpj.replace(/\D/g, '')
      const cpf = (item.cpf || '').toLowerCase()
      const cleanCpf = cpf.replace(/\D/g, '')

      return (
        displayName.includes(cleanTermText) ||
        name.includes(cleanTermText) ||
        cnpj.includes(cleanTermText) ||
        (cleanDigits && cleanCnpj.includes(cleanDigits)) ||
        cpf.includes(cleanTermText) ||
        (cleanDigits && cleanCpf.includes(cleanDigits))
      )
    })
  }

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputItem(newValue)
    setSelectedIndex(0)
    setShowOptions(true)

    if (newValue === '') {
      setItems(allCompanies)
      setClient({ id: 0, companyName: '' })
    } else {
      setItems(filterCompanies(allCompanies, newValue))
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    const trimmed = newValue.replace(/\s*-\s*(Matriz|Filial)$/i, '').trim()
    if (trimmed) {
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`,
            { params: { query: trimmed, limit: 50 } },
          )
          const serverData = Array.isArray(response.data)
            ? response.data
            : response.data?.data || []
          if (Array.isArray(serverData) && serverData.length > 0) {
            setAllCompanies((prev) => {
              const ids = new Set(prev.map((c) => c.id))
              const newOnes = serverData.filter((c: any) => !ids.has(c.id))
              return [...prev, ...newOnes]
            })
            setItems((prev) => {
              const map = new Map<number, CompanyItemType>()
              prev.forEach((c) => map.set(c.id, c))
              serverData.forEach((c: any) => map.set(c.id, c))
              return filterCompanies(Array.from(map.values()), newValue)
            })
          }
        } catch (error) {
          console.error(error)
        }
      }, 250)
    }
  }

  const handleSelectItem = (item: CompanyItemType) => {
    setInputItem(getDisplayName(item))
    setShowOptions(false)
    setClient(item)
  }

  const handleSetOptionByKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' && selectedIndex < items.length - 1) {
      setSelectedIndex((prev) => prev + 1)
      e.preventDefault()
    }
    if (e.key === 'ArrowUp' && selectedIndex > 0) {
      setSelectedIndex((prev) => prev - 1)
      e.preventDefault()
    }
    if (e.key === 'Enter' && items[selectedIndex]) {
      handleSelectItem(items[selectedIndex])
      e.preventDefault()
    }
    if (e.key === 'Tab') setShowOptions(false)
  }

  return (
    <InputDiv>
      <Input
        className="clientToFreight"
        ref={inputRef}
        type="text"
        placeholder="Selecione"
        value={inputItem}
        onFocus={() => {
          isFocusedRef.current = true
          setShowOptions(true)
          if (items.length === 0 && allCompanies.length > 0) {
            setItems(allCompanies)
          } else if (inputItem) {
            const filtered = filterCompanies(allCompanies, inputItem)
            if (filtered.length > 0) {
              setItems(filtered)
            } else {
              setItems(allCompanies)
            }
          }
        }}
        onBlur={() => { isFocusedRef.current = false }}
        onChange={handleChangeInput}
        onKeyDown={(e) => handleSetOptionByKey(e)}
      />
      {showOptions && items.length > 0 && (
        <SearchOptions ref={optionsRef}>
          {items.map((c, index) => (
            <Option
              key={c.id || index}
              onClick={() => handleSelectItem(c)}
              isSelected={index === selectedIndex}
            >
              <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                <span style={{ fontWeight: '500', fontSize: '14px' }}>
                  {getDisplayName(c)}
                </span>
                <span style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                  {getDocument(c) || 'Documento não informado'}
                </span>
              </div>
            </Option>
          ))}
        </SearchOptions>
      )}
    </InputDiv>
  )
}

export default SelectCustomerInFreight


